import type { ReviewReason } from './reviewReason.types';

export type CheckpointTourCopy = {
  shortLabel: string;
  title: string;
  description: string;
  lookFor?: string[];
};

const DEFAULT_REVIEW_POINTS = (label: string): string[] => [
  `Open the compared values for ${label.toLowerCase()}.`,
  'Check they match what the applicant declared.',
  'Note anything that needs follow-up or override.',
];

/** Stable DOM target for a gate checkpoint row. */
export function checkpointTourTarget(gate: string, step: string): string {
  return `checkpoint-${gate.trim().toLowerCase()}-${step.trim().toLowerCase()}`;
}

export function findingTourTarget(findingId: string): string {
  return `finding-${findingId}`;
}

export const EMPLOYER_FOOTPRINT_TOUR_TARGET = 'employer-footprint-panel';
export const EMPLOYER_STREET_VIEW_TOUR_TARGET = 'employer-street-view-stage';
/** First REVIEW/FAIL checkpoint row — used by the guided review-path tour. */
export const EMPLOYER_GATE_ANALYSIS_FIRST_REVIEW_TARGET = 'employer-gate-analysis-first-review';

const STEP_COPY: Record<string, CheckpointTourCopy> = {
  SECTOR_ROLE_MATCH: {
    shortLabel: 'Role fit',
    title: 'Role and industry fit',
    description: 'Check whether the declared job and sector match this employer type.',
  },
  AI_ROLE_SECTOR_ANALYSIS: {
    shortLabel: 'Role fit',
    title: 'Role and industry fit',
    description: 'Review how the declared role lines up with the employer’s sector.',
  },
  DECLARED_SECTOR_VS_EMPLOYER_NAICS: {
    shortLabel: 'Industry',
    title: 'Declared industry vs employer',
    description: 'Compare the customer’s declared industry with the employer’s registered activity.',
  },
  INFRA_MATCH: {
    shortLabel: 'Workplace',
    title: 'Workplace type',
    description: 'Confirm the role fits the employer’s workplace and operating model.',
  },
  EMPLOYMENT_CONTEXT_CONTRADICTION: {
    shortLabel: 'Context',
    title: 'Employment context',
    description: 'Look for contradictions between declared employment and observed signals.',
  },
  ONSITE_ROLE_VS_REMOTE: {
    shortLabel: 'Arrangement',
    title: 'Work arrangement',
    description: 'Check whether onsite vs remote expectations match the role and employer.',
  },
  DIRECT_PAYOR_MATCH: {
    shortLabel: 'Payroll',
    title: 'Payroll source',
    description: 'See whether bank deposits clearly come from the declared employer.',
  },
  PAYROLL_PROVIDER_MATCH: {
    shortLabel: 'Payroll',
    title: 'Payroll provider',
    description: 'Check if a known payroll provider pays the customer for this employer.',
  },
  PERSONAL_TRANSFER_DETECT: {
    shortLabel: 'Deposits',
    title: 'Deposit pattern',
    description: 'Review whether deposits look like employer payroll or personal transfers.',
  },
  GOVERNMENT_SCHEME_PAYOR: {
    shortLabel: 'Payor',
    title: 'Government payor',
    description: 'Check whether government payments explain the income instead of payroll.',
  },
  AI_LINKAGE_ANALYSIS: {
    shortLabel: 'Linkage',
    title: 'Income linkage',
    description: 'Review how bank activity links to the declared employer.',
  },
  DIGITAL_FOOTPRINT: {
    shortLabel: 'Online',
    title: 'Online presence',
    description: 'See whether public web signals corroborate this employer.',
  },
  AI_DIGITAL_FOOTPRINT: {
    shortLabel: 'Profile',
    title: 'Public employer profile',
    description: 'Review public sources that identify and describe this employer.',
  },
  WEB_DOMAIN: {
    shortLabel: 'Website',
    title: 'Company website',
    description: 'Check whether the employer has a credible public website.',
  },
  OPERATIONAL_STATUS: {
    shortLabel: 'Operations',
    title: 'Business operations',
    description: 'See whether the business appears active and operational.',
  },
  PLACE_PHOTOS_VISION: {
    shortLabel: 'Location',
    title: 'Business location photos',
    description: 'Review location photos for signs of an active workplace.',
  },
  SATELLITE_BUILDING_VISION: {
    shortLabel: 'Site',
    title: 'Worksite imagery',
    description: 'Check satellite imagery for a plausible employer site.',
  },
  ACTIVE_DIRECTORS: {
    shortLabel: 'Directors',
    title: 'Business leadership',
    description: 'Review director and leadership signals for business legitimacy.',
  },
  REGISTRY_STATUS: {
    shortLabel: 'Registry',
    title: 'Business registration',
    description: 'Confirm whether the employer is registered and active.',
  },
  EMPLOYER_LOOKUP: {
    shortLabel: 'Lookup',
    title: 'Employer lookup',
    description: 'See whether the declared employer was found in available records.',
  },
  ADDRESS_VERIFICATION: {
    shortLabel: 'Address',
    title: 'Employer address',
    description: 'Check whether the employer address on file is verified.',
  },
  PHONE_VERIFICATION: {
    shortLabel: 'Phone',
    title: 'Employer phone',
    description: 'Review phone contact signals for the employer.',
  },
  COMMUTE_DISTANCE: {
    shortLabel: 'Commute',
    title: 'Commute distance',
    description: 'Check if the customer can realistically reach this workplace.',
    lookFor: [
      'Compare commute km against the policy limit shown.',
      'Confirm whether the role is onsite, hybrid, or remote.',
      'Decide if the distance needs override, review, or decline.',
    ],
  },
  CROSS_PROVINCE_COMMUTE: {
    shortLabel: 'Commute',
    title: 'Cross-province commute',
    description: 'Long-distance commutes need extra scrutiny.',
    lookFor: [
      'Check if the customer lives in a different province than the employer.',
      'Ask whether regular travel for this role is realistic.',
      'Use map and address evidence to confirm the worksite location.',
    ],
  },
  EMPLOYER_ADDRESS: {
    shortLabel: 'Address',
    title: 'Work location',
    description: 'Make sure the worksite matches what was declared.',
    lookFor: [
      'Compare declared address with registry and map pins.',
      'Check Street View or satellite for a plausible workplace.',
      'Flag if the site type does not fit the job or employer.',
    ],
  },
  FUTURE_START_DATE: {
    shortLabel: 'Start date',
    title: 'Employment start',
    description: 'Check whether the start date is in the future or otherwise unusual.',
  },
  MUTATION_CHECK: {
    shortLabel: 'History',
    title: 'Employment changes',
    description: 'Review recent changes to declared employment details.',
  },
  WORK_AUTHORIZATION: {
    shortLabel: 'Authorization',
    title: 'Work authorization',
    description: 'Check work authorization timing against employment dates.',
  },
  INCORPORATION_DATE: {
    shortLabel: 'Tenure',
    title: 'Company age',
    description: 'See whether company age supports the claimed employment tenure.',
  },
  EMPLOYMENT_ELIGIBILITY: {
    shortLabel: 'Eligibility',
    title: 'Employment eligibility',
    description: 'Review whether employment timing is eligible under policy.',
  },
};

const CATEGORY_FALLBACK: Record<
  ReviewReason['category'],
  CheckpointTourCopy
> = {
  ROLE: {
    shortLabel: 'Role',
    title: 'Role evidence',
    description: 'Review the check that flagged a role or industry concern.',
  },
  INCOME: {
    shortLabel: 'Income',
    title: 'Income evidence',
    description: 'Review the check that flagged an income or payroll concern.',
  },
  DIGITAL_PRESENCE: {
    shortLabel: 'Public',
    title: 'Public evidence',
    description: 'Review the public information check behind this concern.',
  },
  EMPLOYER: {
    shortLabel: 'Employer',
    title: 'Employer evidence',
    description: 'Review the employer legitimacy check behind this concern.',
  },
  LOCATION: {
    shortLabel: 'Location',
    title: 'Location evidence',
    description: 'Review the location or commute check behind this concern.',
  },
  TENURE: {
    shortLabel: 'Tenure',
    title: 'Tenure evidence',
    description: 'Review the employment timing check behind this concern.',
  },
};

/** Reviewer-facing tour copy — no backend checkpoint names in primary text. */
export function getCheckpointTourCopy(
  step: string,
  finding: ReviewReason,
): CheckpointTourCopy & { lookFor: string[] } {
  const key = step.trim().toUpperCase();
  const copy = STEP_COPY[key] ?? CATEGORY_FALLBACK[finding.category];
  return {
    ...copy,
    lookFor: copy.lookFor ?? DEFAULT_REVIEW_POINTS(copy.shortLabel),
  };
}
